import React, { useState } from "react";
import {
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiChevronUp,
  FiDollarSign,
  FiGrid,
  FiPackage,
  FiPercent,
  FiTag,
} from "react-icons/fi";
import {
  CardContainer,
  CardContent,
  CardHeader,
  CheckboxWrapper,
  CoreInfoRow,
  CurrencySymbol,
  DiscountedPrice,
  DiscountLabelMinimal,
  DiscountLabel,
  DiscountRow,
  DiscountSection,
  ExpandHint,
  FallbackImage,
  HeaderLeft,
  HeaderRight,
  HiddenCheckbox,
  ImageContainer,
  InputWrapper,
  MetaItem,
  NewPriceBadge,
  NoVariations,
  PriceBadge,
  PriceContainer,
  PriceLabel,
  ProductInfo,
  ProductMeta,
  ProductTitle,
  SelectedBadge,
  StrikethroughPrice,
  StyledCheckbox,
  StyledInput,
  StyledSelect,
  VariationCard,
  VariationControls,
  VariationCurrency,
  VariationsHeader,
  VariationsSection,
  VariationInfo,
  VariationInput,
  VariationMeta,
  VariationPricePreview,
  VariationRow,
  VariationSelect,
  VariationTitle,
  VariationsGrid,
} from "./style";

interface ProductCardProps {
  item: any;
  toggleSelection: (id: string) => void;
  updateProductDiscount: (id: string, field: string, value: any) => void;
  updateVariationDiscount: (
    productId: string,
    variationId: string,
    field: string,
    value: any,
  ) => void;
  bucketUrl: string;
  settings: any;
  currentDealId?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  item,
  toggleSelection,
  updateProductDiscount,
  updateVariationDiscount,
  bucketUrl,
  settings,
  currentDealId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const activeDeal = item.activeDeal;
  // Check if product is in an active deal AND it is NOT the current deal we are editing
  const isConflict =
    activeDeal &&
    (currentDealId
      ? String(activeDeal.dealId) !== String(currentDealId)
      : true);

  // Block selection only if it's not already selected
  const isBlocked = isConflict && !item.isSelected;

  const handleToggle = () => {
    if (isBlocked) return;
    toggleSelection(item.id);
  };

  return (
    <CardContainer
      isSelected={item.isSelected}
      isHovered={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {item.isSelected && (
        <SelectedBadge>
          <FiCheck size={10} />
          Selected
        </SelectedBadge>
      )}

      {/* Header: Title & Checkbox */}
      <CardHeader
        isSelected={item.isSelected}
        onClick={() => setExpanded(!expanded)}
      >
        <HeaderLeft>
          <CheckboxWrapper>
            <HiddenCheckbox
              type="checkbox"
              checked={item.isSelected}
              onChange={handleToggle}
              disabled={isBlocked}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            <StyledCheckbox
              isSelected={item.isSelected}
              style={
                isBlocked
                  ? {
                      background: "#e5e7eb",
                      borderColor: "#d1d5db",
                      cursor: "not-allowed",
                    }
                  : {}
              }
            >
              {item.isSelected && <FiCheck size={12} color="white" />}
            </StyledCheckbox>
          </CheckboxWrapper>
          <ProductInfo>
            <ProductTitle isSelected={item.isSelected}>
              <FiPackage size={14} />
              {item.name}
            </ProductTitle>
            <ProductMeta>
              <MetaItem>
                <FiTag size={10} />
                {item.category?.name}
              </MetaItem>
              {item.brand?.name && (
                <>
                  <span style={{ color: "#d1d5db" }}>•</span>
                  <span>{item.brand.name}</span>
                </>
              )}
            </ProductMeta>
          </ProductInfo>
        </HeaderLeft>
        <HeaderRight>
          {isConflict ? (
            <div
              style={{
                fontSize: "12px",
                color: "#dc2626",
                background: "#fef2f2",
                padding: "4px 8px",
                borderRadius: "4px",
                border: "1px solid #fecaca",
                marginRight: "8px",
                fontWeight: 500,
              }}
            >
              In Deal: {activeDeal?.dealName}
            </div>
          ) : (
            <PriceBadge>
              {item.price > (item.priceBeforeDeal || item.price) && (
                <PriceContainer>
                  <PriceLabel>MRP</PriceLabel>
                  <StrikethroughPrice>Rs {item.price}</StrikethroughPrice>
                </PriceContainer>
              )}
              {item.discountValue > 0 ? (
                <>
                  <PriceContainer>
                    <PriceLabel>
                      {item.discountType === "percentage"
                        ? `${item.discountValue}% Discount`
                        : `Rs ${item.discountValue} Discount`}
                    </PriceLabel>
                    <StrikethroughPrice>
                      Rs {item.priceBeforeDeal || item.price}
                    </StrikethroughPrice>
                  </PriceContainer>
                  <PriceContainer>
                    <PriceLabel>Deal Price</PriceLabel>
                    <DiscountedPrice>
                      Rs{" "}
                      {item.discountType === "percentage"
                        ? (
                            (item.priceBeforeDeal || item.price) *
                            (1 - item.discountValue / 100)
                          ).toFixed(2)
                        : (
                            (item.priceBeforeDeal || item.price) -
                            item.discountValue
                          ).toFixed(2)}
                      <DiscountLabelMinimal>
                        {item.discountType === "percentage"
                          ? `${item.discountValue}% OFF`
                          : `Rs ${item.discountValue} OFF`}
                      </DiscountLabelMinimal>
                    </DiscountedPrice>
                  </PriceContainer>
                </>
              ) : (
                <PriceContainer>
                  <PriceLabel>After Discount</PriceLabel>
                  <DiscountedPrice>
                    Rs {item.priceBeforeDeal || item.price}
                  </DiscountedPrice>
                </PriceContainer>
              )}
            </PriceBadge>
          )}
          {expanded ? (
            <FiChevronUp size={18} color="#9ca3af" />
          ) : (
            <FiChevronDown size={18} color="#9ca3af" />
          )}
        </HeaderRight>
      </CardHeader>

      {/* Main Content */}
      <CardContent>
        {/* Core Info Row */}
        <CoreInfoRow>
          {/* Image */}
          {item.images?.[0]?.image ? (
            <ImageContainer>
              <img
                src={`${bucketUrl}/${item.images[0].image}`}
                alt={item.name}
              />
            </ImageContainer>
          ) : (
            <FallbackImage>{item.name?.charAt(0)?.toUpperCase()}</FallbackImage>
          )}

          {/* Discount Controls */}
          <DiscountSection>
            <DiscountLabel>
              <FiPercent size={12} />
              Set Discount
            </DiscountLabel>

            <DiscountRow>
              <StyledSelect
                value={item.discountType || "percentage"}
                onChange={(e) =>
                  updateProductDiscount(item.id, "discountType", e.target.value)
                }
              >
                <option value="percentage">Percentage %</option>
                <option value="amount">Fixed Amount</option>
              </StyledSelect>

              <InputWrapper>
                <StyledInput
                  type="number"
                  value={item.discountValue}
                  onChange={(e) =>
                    updateProductDiscount(
                      item.id,
                      "discountValue",
                      e.target.value,
                    )
                  }
                  placeholder="0"
                />
                <CurrencySymbol>
                  {item.discountType === "percentage" ? "%" : "Rs"}
                </CurrencySymbol>
              </InputWrapper>

              <NewPriceBadge>
                Deal price:{" "}
                <span>
                  {item.discountType === "percentage"
                    ? `Rs ${(
                        (item.priceBeforeDeal || item.price) *
                        (1 - item.discountValue / 100)
                      ).toFixed(2)}`
                    : `Rs ${(
                        (item.priceBeforeDeal || item.price) -
                        item.discountValue
                      ).toFixed(2)}`}
                </span>
                {item.discountValue > 0 && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "10px",
                      color: "#10b981",
                      fontWeight: 600,
                      background: "#d1fae5",
                      padding: "2px 6px",
                      borderRadius: "4px",
                    }}
                  >
                    {item.discountType === "percentage"
                      ? `${item.discountValue}% OFF`
                      : `Rs ${item.discountValue} OFF`}
                  </span>
                )}
              </NewPriceBadge>
            </DiscountRow>
          </DiscountSection>
        </CoreInfoRow>

        {/* Variations Section */}
        {expanded && (
          <VariationsSection>
            <VariationsHeader>
              <FiGrid size={14} />
              Product Variations ({item.variations?.length || 0})
            </VariationsHeader>

            {item.variations && item.variations.length > 0 ? (
              <VariationsGrid>
                {item.variations.map((v: any, idx: number) => (
                  <VariationCard key={idx}>
                    {/* Top Row: Variation Info */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "12px",
                        paddingBottom: "12px",
                        borderBottom: "1px dashed #e5e7eb",
                      }}
                    >
                      {/* Variation Image */}
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "6px",
                          border: "1px solid #e5e7eb",
                          overflow: "hidden",
                          flexShrink: 0,
                          background: "#f9fafb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {v.image ? (
                          <img
                            src={`${bucketUrl}/${v.image}`}
                            alt="variant"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: "10px",
                              color: "#9ca3af",
                              fontWeight: 500,
                            }}
                          >
                            No Img
                          </span>
                        )}
                      </div>

                      {/* Variation Info */}
                      <VariationInfo>
                        <VariationTitle>
                          {v.color?.name || v.color?.code || "Variant"}{" "}
                          {v.attributes?.[0]?.value
                            ? `• ${v.attributes[0].value}`
                            : ""}
                        </VariationTitle>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "11px",
                            color: "#6b7280",
                            marginTop: "2px",
                            flexWrap: "wrap",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "3px",
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>Price:</span>
                            Rs {v.price}
                          </div>

                          {(v.stock || v.stock === 0) && (
                            <>
                              <span style={{ color: "#d1d5db" }}>|</span>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "3px",
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>Stock:</span>
                                <span
                                  style={{
                                    color: v.stock < 5 ? "#ef4444" : "#6b7280",
                                    fontWeight: v.stock < 5 ? 600 : 400,
                                  }}
                                >
                                  {v.stock}
                                </span>
                              </div>
                            </>
                          )}

                          {v.sku && (
                            <>
                              <span style={{ color: "#d1d5db" }}>|</span>
                              <div
                                style={{
                                  maxWidth: "100px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={v.sku}
                              >
                                SKU: {v.sku}
                              </div>
                            </>
                          )}
                        </div>
                      </VariationInfo>
                    </div>

                    {/* Bottom Row: Controls */}
                    <VariationRow style={{ justifyContent: "space-between" }}>
                      {/* Variation Discount Controls */}
                      <VariationControls>
                        <VariationSelect
                          value={v.discountType || "percentage"}
                          onChange={(e) =>
                            updateVariationDiscount(
                              item.id,
                              v.id || v._id,
                              "discountType",
                              e.target.value,
                            )
                          }
                        >
                          <option value="percentage">%</option>
                          <option value="amount">Flat</option>
                        </VariationSelect>

                        <div style={{ position: "relative" }}>
                          <VariationInput
                            type="number"
                            value={v.discountValue}
                            onChange={(e) =>
                              updateVariationDiscount(
                                item.id,
                                v.id || v._id,
                                "discountValue",
                                e.target.value,
                              )
                            }
                          />
                          <VariationCurrency>
                            {v.discountType === "percentage" ? "%" : "Rs"}
                          </VariationCurrency>
                        </div>
                      </VariationControls>
                    </VariationRow>

                    {/* New Price Preview */}
                    <VariationPricePreview>
                      <PriceBadge
                        style={{
                          width: "100%",
                          background: "transparent",
                          border: "none",
                          padding: 0,
                        }}
                      >
                        {v.price > (v.priceBeforeDeal || v.price) && (
                          <PriceContainer>
                            <PriceLabel>MRP</PriceLabel>
                            <StrikethroughPrice style={{ fontSize: "10px" }}>
                              Rs {v.price}
                            </StrikethroughPrice>
                          </PriceContainer>
                        )}
                        {v.discountValue > 0 ? (
                          <>
                            <PriceContainer>
                              <PriceLabel>
                                {v.discountType === "percentage"
                                  ? `${v.discountValue}% Discount`
                                  : `Rs ${v.discountValue} Discount`}
                              </PriceLabel>
                              <StrikethroughPrice style={{ fontSize: "10px" }}>
                                Rs {v.priceBeforeDeal || v.price}
                              </StrikethroughPrice>
                            </PriceContainer>
                            <PriceContainer>
                              <PriceLabel>Deal Price</PriceLabel>
                              <DiscountedPrice style={{ fontSize: "12px" }}>
                                Rs{" "}
                                {v.discountType === "percentage"
                                  ? (
                                      (v.priceBeforeDeal || v.price) *
                                      (1 - v.discountValue / 100)
                                    ).toFixed(2)
                                  : (
                                      (v.priceBeforeDeal || v.price) -
                                      v.discountValue
                                    ).toFixed(2)}
                                <DiscountLabelMinimal
                                  style={{ fontSize: "8px", color: "white" }}
                                >
                                  {v.discountType === "percentage"
                                    ? `${v.discountValue}% OFF`
                                    : `Rs ${v.discountValue} OFF`}
                                </DiscountLabelMinimal>
                              </DiscountedPrice>
                            </PriceContainer>
                          </>
                        ) : (
                          <PriceContainer>
                            <PriceLabel>After Discount</PriceLabel>
                            <DiscountedPrice style={{ fontSize: "12px" }}>
                              Rs {v.priceBeforeDeal || v.price}
                            </DiscountedPrice>
                          </PriceContainer>
                        )}
                      </PriceBadge>
                    </VariationPricePreview>
                  </VariationCard>
                ))}
              </VariationsGrid>
            ) : (
              <NoVariations>
                No variations available for this product
              </NoVariations>
            )}
          </VariationsSection>
        )}

        {/* Expand/Collapse Hint */}
        {!expanded && item.variations?.length > 0 && (
          <ExpandHint onClick={() => setExpanded(true)}>
            <FiChevronRight size={12} />
            Show {item.variations.length} variation
            {item.variations.length !== 1 ? "s" : ""}
          </ExpandHint>
        )}
      </CardContent>
    </CardContainer>
  );
};

export default ProductCard;
